import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { Content, ContentDatabaseModifier, ContentVisibility, FolderParent } from '@efficiently/models';

import { ContentsRepository, ContentUploadService, TagsParams, UploadParams } from '@efficiently/client-api-content';
import { SEMICOLON_SEPARATOR } from '@efficiently/client-util-constants';

@Injectable()
export class DefaultContentUploadService extends ContentUploadService {
	private readonly contentsRepository = inject(ContentsRepository);

	public upload(
		data: UploadParams,
		modifier: ContentDatabaseModifier = ContentDatabaseModifier.STANDARD_MODE,
	): Observable<Content[]> {
		const params = this.getUploadParameters(data.project?._id, data.folder);
		const folderId = params?.folderId;
		const folderParent = params.folderParent;
		const projectOrgId = data.project?._id == null ? null : data.project.orgId;
		const projectOrgCode = data.project?._id == null ? null : data.project.orgCode;
		const parentId = data.parentId || data.project?._id;
		const parentType = data.project?._id == null ? null : FolderParent.Project;

		return this.contentsRepository.uploadContents(
			data.files,
			data.project?._id,
			projectOrgId,
			projectOrgCode,
			folderId,
			folderParent,
			parentId,
			parentType,
			data.documentType,
			data.visibility,
			modifier,
		);
	}

	public parseFolderPath(projectId: string, folder: string): TagsParams {
		const parts = folder.split(SEMICOLON_SEPARATOR);
		return {
			projectId,
			folderId: parts[0],
			folderParent: parts.length < 2 ? null : parts[1],
			externalIds: [],
		};
	}

	public getUploadParameters(projectId: string, folder: string): TagsParams {
		let params: TagsParams = {
			folderId: null,
			projectId: null,
			externalIds: [],
		};
		if (folder) {
			params = this.parseFolderPath(projectId, folder);
			// EFF-24748 dead code — no caller reads params.projectId: upload() and images-manager take
			// only folderId and folderParent from the result.
			if (params.projectId && params.folderParent === FolderParent.Organization) {
				params.projectId = null;
			}
		}
		return params;
	}

	public uploadScreenshots(
		files: File[],
		projectId?: string,
		projectOrgId?: string,
		projectOrgCode?: string,
		visibility?: ContentVisibility,
	): Observable<Content[]> {
		return this.contentsRepository.uploadContents(
			files,
			projectId,
			projectOrgId,
			projectOrgCode,
			null,
			null,
			null,
			null,
			null,
			visibility,
			ContentDatabaseModifier.SCREENSHOT_MODE,
		);
	}
}
